///////////////////
// Favorite Store
//////////////////
class FavoriteStore {
  constructor() {
    this.latestFavoriteToken = 0;
    this.init();
  }

  init() {
    // Add document-level click listener for all fav-location elements
    document.addEventListener('click', (event) => {
      const favIcon = event.target.closest('.fav-location');
      if (!favIcon) return;

      const favoriteContainer = favIcon.closest('[data-favorite-store]');
      if (!favoriteContainer) return;

      const storeHandle = favoriteContainer.getAttribute('data-favorite-store');
      const isCurrentlyFav = localStorage.getItem('tg--webcheckin-fav') === storeHandle;

      // If the clicked store is already the favorite, remove it. Otherwise, set as favorite.
      document.dispatchEvent(new CustomEvent('favorite-store:change', {
        detail: {
          storeHandle,
          isFavorite: !isCurrentlyFav && !!storeHandle // false if already favorite, true if not
        }
      }));
    });

    // Listen for favorite store changes
    document.addEventListener('favorite-store:change', this.handleFavoriteChange.bind(this));

    // Sync with page barbershop ID on load
    this.syncWithPageBarbershop();
  }

  async handleFavoriteChange(event) {
    // Generate a new token for this favorite action
    const myToken = ++this.latestFavoriteToken;

    const { storeHandle, isFavorite } = event.detail;
    const prevFav = localStorage.getItem('tg--webcheckin-fav');

    // Optimistically update UI
    this.updateFavoriteUI(storeHandle, isFavorite);

    // Try to update localStorage and cart
    const success = await this.updateFavoriteStore(isFavorite ? storeHandle : null, myToken);

    // If this is not the latest favorite action, abort
    if (myToken !== this.latestFavoriteToken) return;

    // If update failed, revert UI to previous state
    if (!success) {
      this.updateFavoriteUI(prevFav, !!prevFav);
    }
  }

  async updateFavoriteStore(storeHandle, token) {
    // Update localStorage
    if (storeHandle) {
      localStorage.setItem('tg--webcheckin-fav', storeHandle);
    } else {
      localStorage.removeItem('tg--webcheckin-fav');
    }

    // Update cart attribute
    const success = await this.updateCartAttribute(storeHandle);

    // Re-render sections if cart update was successful
    if (success) {
      await this.renderSection([
        { sectionId: 'header', selector: '.header-location' },
        { sectionId: 'header', selector: '#barbershop-drawer' }
      ], token ?? this.latestFavoriteToken);
    }

    // Log current state
    // console.log('Favorite Store Updated:', storeHandle || 'none');

    return success;
  }

  updateFavoriteUI(storeHandle, isFavorite) {
    // Remove favorite class from all elements
    document.querySelectorAll('.is-fav').forEach(elem => elem.classList.remove('is-fav'));

    // Add favorite class if needed
    if (isFavorite) {
      document.querySelectorAll(`[data-favorite-store="${storeHandle}"]`).forEach(e => {
        e.classList.add('is-fav');
        // Animate the card to the top of its list
        // For main locator, animate li.location
        if (e.closest('.locations-nearest__list')) {
          const card = e.closest('li.location');
          if (card && card.parentElement.firstChild !== card) {
            flipMoveToTopGeneric(card, '.locations-nearest__list');
          }
        }
        // For city-lp, animate .city-wrap
        else if (e.closest('.locations-lp')) {
          const cityWrap = e.closest('.city-wrap');
          if (cityWrap && cityWrap.parentElement.firstChild !== cityWrap) {
            flipMoveToTopGeneric(cityWrap, '.locations-lp');
          }
        }
        // Add more selectors as needed for other contexts
      });
    }
  }

  async updateCartAttribute(storeHandle) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            'Favorite Store': storeHandle
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      return true;
    } catch (error) {
      console.error('Error updating cart attribute:', error);
      return false;
    }
  }

  async renderSection(selectors, token) {
    if (!selectors.length) return;
    // If a new favorite action has started, abort this render
    if (token !== this.latestFavoriteToken) return;

    // Initially we had this in a for loop so we could fetch
    // individual sections, but for performance I consolidated
    // my barbershop drawer and button into header.
    const { sectionId } = selectors[0];
    const response = await fetch(`/?section_id=${sectionId}`);
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    for (const { selector } of selectors) {
      // If a new favorite action has started, abort this render
      if (token !== this.latestFavoriteToken) return;
      const newElement = doc.querySelector(selector);
      const currentElement = document.querySelector(selector);

      if (newElement && currentElement) {
        currentElement.replaceWith(newElement);

        // Replace all barbershop buttons with new HTML
        if (selector === '.header-location') {
          // Find all existing barbershop buttons and replace them
          const allButtons = document.querySelectorAll('.barbershop-button');
          allButtons.forEach(button => {
            button.outerHTML = newElement.outerHTML;
          });

          // Remove loading class from all updated buttons after replacement
          requestAnimationFrame(() => {
            document.querySelectorAll('.barbershop-button .content').forEach(content => {
              content.classList.remove('loading-location');
            });
          });
        }

        // Dispatch event if this is the barbershop drawer
        if (selector === '#barbershop-drawer') {
          document.dispatchEvent(new CustomEvent('barbershop-drawer:after:rendered'));
        }
      }
    }
  }

  async syncWithPageBarbershop() {
    const favStore = localStorage.getItem('tg--webcheckin-fav');
    const pageFavoriteStore = document.documentElement.getAttribute('data-barbershop-id');
    // console.log('LocalStorage Favorite Store:', favStore);
    // console.log('Page Favorite Store:', pageFavoriteStore);

    // If localStorage is "undefined" or empty
    if (!favStore || favStore === 'undefined') {
      // If there's a favorite store set in the page, remove it
      if (pageFavoriteStore) {
        const success = await this.updateCartAttribute(null);
        if (success) {
          // console.log('renderingSections');
          await this.renderSection([
            { sectionId: 'header', selector: '.header-location' },
            { sectionId: 'header', selector: '#barbershop-drawer' }
          ]);
        }
        // console.log('Removed favorite store as localStorage is empty');
      }
      return;
    }

    // If page favorite store doesn't exist or doesn't match localStorage
    if (!pageFavoriteStore || pageFavoriteStore !== favStore) {
      const success = await this.updateCartAttribute(favStore);
      if (success) {
        await this.renderSection([
          { sectionId: 'header', selector: '.header-location' },
          { sectionId: 'header', selector: '#barbershop-drawer' }
        ]);
      }
      // console.log('Updated page favorite store to match localStorage:', favStore);
    } else {
      // If everything matches, just update the UI without re-rendering
      this.updateFavoriteUI(favStore, true);
    }
  }
}

///////////////////
// Nav Drawer
//////////////////
async function navDrawer() {
  // const buttons = document.querySelectorAll('[data-nav-drawer]');
  const buttons = document.querySelectorAll('.has-drawer-nav summary');
  const navDrawer = document.getElementById('nav-drawer');
  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      if (theme.mediaMatches.md) {
        navDrawer.open(button);
      }
    });
  });

  // Appends drawer nav to body (once) before open, to avoid z-index issues with overlay and header.
  if (navDrawer.parentElement !== document.body) {
    document.body.appendChild(navDrawer);
  }

  // Somewhere in your theme initialization
  theme.navDrawerIsOpen = false;

  navDrawer.addEventListener('on:nav-drawer:before-open', (event) => {
    // console.log('Drawer opening!', event);
    theme.navDrawerIsOpen = true;
  });
  navDrawer.addEventListener('on:nav-drawer:after-open', (event) => {
    // console.log('Drawer is open!', event);
  });
  navDrawer.addEventListener('on:nav-drawer:after-close', (event) => {
    // console.log('Drawer closed!', event);
    theme.navDrawerIsOpen = false;
  });

  // Attach a single event listener to the nav-drawer container
  navDrawer.addEventListener('click', (event) => {
    // Scroll all ul elements inside navDrawer to top
    navDrawer.querySelectorAll('ul').forEach(ul => {
      ul.scrollTo({ top: 0, behavior: 'smooth' });
    });

    const target = event.target;

    if (target.matches('.nav-back')) {
      handleBackClick(target);
    } else if (target.matches('.nested-nav')) {
      handleNavItemClick(target);
    }
  });
}

function handleNavItemClick(navItem) {
  const li = navItem.closest('li');
  if (!li) return;
  // Using :scope > ul ensures we only get the direct child menu
  const childMenu = li.querySelector(':scope > ul');
  // const parentUl = navItem.closest('ul');
  // parentUl.scrollTop = 0;
  if (childMenu && childMenu.getAttribute('data-visible') === 'false') {
    childMenu.setAttribute('data-visible', 'true');
    childMenu.setAttribute('open', '');
  }
}

function handleBackClick(backButton) {
  // The parent of the back button is the menu <ul> that we want to close.
  const parentMenu = backButton.parentElement;
  if (parentMenu && parentMenu.hasAttribute('data-visible')) {
    // Close any open child menus within the parent menu.
    const openSubMenus = parentMenu.querySelectorAll('ul[open]');
    openSubMenus.forEach(submenu => {
      submenu.setAttribute('data-visible', 'false');
      submenu.removeAttribute('open');
    });
    // Now close the parent menu itself.
    parentMenu.setAttribute('data-visible', 'false');
    parentMenu.removeAttribute('open');
  }
}

/**
 * Animate moving a card to the top of a list using FLIP.
 * @param {Element} card - The card element to move (optional if newOrderElements is provided).
 * @param {string} containerSelector - Selector for the parent container.
 * @param {boolean} stagger - Whether to stagger the animation (default: false).
 * @param {boolean} moveToTop - Whether to move card to top (default: true). Ignored if newOrderElements is present.
 * @param {Array<Element>} newOrderElements - Array of elements in their new order (optional).
 */
function flipMoveToTopGeneric(card, containerSelector, stagger = false, moveToTop = true, newOrderElements = null) {
  // If we have new elements, getting parent from the first one is safer than card which might be null
  const parent = newOrderElements && newOrderElements.length > 0
    ? newOrderElements[0].parentElement
    : (card ? card.closest(containerSelector) : document.querySelector(containerSelector));

  if (!parent) return;

  const cards = Array.from(parent.children);

  // 1. Record the first positions of all cards
  const firstRects = cards.map(c => c.getBoundingClientRect());

  // 2. Perform DOM updates
  if (newOrderElements) {
    // Reappend all elements in new order
    newOrderElements.forEach(el => parent.appendChild(el));
  } else if (moveToTop && card) {
    parent.insertBefore(card, parent.firstChild);
  }

  // 3. Record the last positions of all cards
  const lastRects = Array.from(parent.children).map(c => c.getBoundingClientRect());

  // 4. For each card, apply the FLIP transform
  const movingCards = new Set();
  const currentChildren = Array.from(parent.children);

  // Note: cards array is still the original order, which matches firstRects indices
  cards.forEach((c, i) => {
    // Find where this card ended up
    const lastIndex = currentChildren.indexOf(c);
    const dy = firstRects[i].top - lastRects[lastIndex].top;

    // Only animate if the movement is significant (> 1px) and UPWARDS (positive dy)
    // Positive dy means start (firstRect) > end (lastRect), so it moved UP
    if (dy > 1) {
      movingCards.add(c);
      c.style.transition = 'none';
      c.style.transform = `translateY(${dy}px)`;
    } else {
      c.style.transform = '';
    }
  });

  // Force reflow
  parent.offsetHeight;

  // 5. Animate all cards to their new positions
  // We define the animation on the elements themselves
  Array.from(parent.children).forEach((c, i) => {
    // Skip if not moving
    if (!movingCards.has(c)) {
      c.style.transform = '';
      return;
    }

    // If we reordered, the index i is the NEW position (0, 1, 2...)
    // Stagger based on visual position
    const delay = stagger ? i * 30 : 0;

    if (delay > 0) {
      c.style.transitionDelay = `${delay}ms`;
    }
    // Add animating class for styling (e.g. z-index)
    c.classList.add('is-animating');

    c.style.transition = 'transform 0.4s cubic-bezier(.4,0,.2,1)';
    c.style.transform = '';

    const handler = (e) => {
      if (e.propertyName === 'transform') {
        c.classList.remove('is-animating');
        c.style.transition = '';
        c.style.transitionDelay = '';
        c.style.transform = '';
        c.removeEventListener('transitionend', handler);
      }
    };
    c.addEventListener('transitionend', handler);
  });

  // Scroll logic
  if (moveToTop && !newOrderElements) { // Only scroll if it's a "move to top" action
    const target = card || parent;
    const scrollContainer = target.closest('.locations.scrollbar');
    if (scrollContainer && scrollContainer.scrollTo) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (window.innerWidth < 991 || !scrollContainer) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}

document.addEventListener("DOMContentLoaded", function () {
  navDrawer();

  // Initialize favorite store functionality
  window.favoriteStore = new FavoriteStore();

  // Listen for FLIP animation requests
  document.addEventListener('dispatch:flip:reorder-cards', (event) => {
    const { containerSelector, stagger, newOrderElements } = event.detail;
    if (containerSelector || newOrderElements) {
      // If we have elements, we can infer selector from parent, so allow null selector
      flipMoveToTopGeneric(null, containerSelector, stagger, false, newOrderElements);
    }
  });
});